import './AppointmentActions.scss';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PropTypes from 'prop-types';

import {
  acceptAppointment,
  arrivedAppointment,
  loadAppointment,
} from 'actions/appointments';
import Button from 'atoms/button';
import Dropdown from 'atoms/dropdown';
import toast from 'atoms/toast';
import useQuery from 'hooks/useQuery';
import useWindowDimensions from 'hooks/useWindowDimensions';
import {
  getAppointmentLastAction,
  isAppointmentCompleted,
} from 'pages/Appointments/util';
import { updateSingleAppointment } from 'store/reducers/providerAppointments';
import {
  openAppointmentActionsModal,
  openDrawer,
  openNotifyClientModal,
  toggleLateCancelModal,
  toggleNoShowModal,
} from 'store/reducers/ui';
import {
  isDateWithinLast3Days,
  isInThePast,
  isLessThan24HoursAway,
  isPastDateTime,
  isToday,
} from 'util';

import {
  modalsInfo,
  refreshAppointments,
  trackAppointmentAction,
} from './util';

export default function AppointmentActions({
  appointment,
  type = 'list',
  dropdownOnly,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const query = useQuery();
  const { width } = useWindowDimensions();
  const isMobile = width <= 600;

  const { dateStart, status, uuid } = appointment;

  const lastAction = getAppointmentLastAction(appointment);
  const isRequested = status?.value === 'requested';
  const isConfirmed = !isRequested;
  const isCompleted = isAppointmentCompleted(appointment);

  const canMarkAsArrived = isConfirmed && isToday(dateStart) && !lastAction?.id;
  const hasArrived = lastAction?.value === 'arrived';
  const canMarkAsNoShow =
    isToday(dateStart) && isPastDateTime(dateStart) && !hasArrived;
  const canCancel = !isInThePast(dateStart) && !hasArrived && !isCompleted;
  const canReschedule = !isInThePast(dateStart) && !hasArrived && !isCompleted;
  const canReassign = true;
  const canCheckout =
    isConfirmed &&
    (hasArrived || (!isToday(dateStart) && isDateWithinLast3Days(dateStart)));
  const canNotifyClient = true;

  const onAcceptAppointment = () => {
    dispatch(acceptAppointment(uuid)).then(({ success }) => {
      if (success) {
        refreshAppointments({
          location,
          query,
          dispatch,
          auid: appointment?.animal?.uuid,
        });
        toast({ text: 'Appointment accepted!' });
      }
    });
  };

  const onNoShow = () => {
    onDropdownClick('No show');
  };

  const onArrived = () => {
    dispatch(arrivedAppointment(uuid)).then(({ success }) => {
      if (success) {
        dispatch(
          loadAppointment({
            apuid: uuid,
            actions: [null, updateSingleAppointment, null],
          })
        );
        toast({ text: 'Customer arrived!' });
      }
    });
  };

  const onCheckout = () => {
    dispatch(openDrawer({ type: 'payment', uuid }));
  };

  const getDropdownItems = () => {
    if (isRequested) return ['Reschedule', 'Decline'];

    const dropdownActions = [];
    if (canReschedule) dropdownActions.push('Reschedule');
    if (canReassign) dropdownActions.push('Reassign');
    if (canCancel) dropdownActions.push('Cancel');
    if (canMarkAsNoShow) dropdownActions.push('No show');
    if (canNotifyClient) dropdownActions.push('Notify client');

    return dropdownActions;
  };

  const dropdownItems = getDropdownItems();

  const onDropdownClick = (value) => {
    const isLastMinute = isLessThan24HoursAway(
      new Date(appointment.dateStart),
      new Date()
    );

    trackAppointmentAction(value);
    if (value === 'Reschedule' || value === 'Reassign') {
      dispatch(openDrawer({ type: value.toLowerCase(), uuid }));
      return;
    }

    if (value === 'Cancel' && isLastMinute) {
      return dispatch(toggleLateCancelModal({ isOpen: true, appointment }));
    }

    if (value === 'No show') {
      return dispatch(toggleNoShowModal({ isOpen: true, appointment }));
    }

    if (value === 'Notify client') {
      return dispatch(openNotifyClientModal({ appointment }));
    }

    dispatch(
      openAppointmentActionsModal({
        modalData: modalsInfo(appointment)[value],
      })
    );
  };

  const buttonSize =
    type === 'list' ? 'small' : isMobile ? 'medium-small' : 'medium';

  if (isCompleted) return null;

  return (
    <div className={`appointment-actions ${type}`}>
      {!dropdownOnly && (
        <>
          {isRequested && (
            <Button
              size={buttonSize}
              variant="reverse"
              onClick={onAcceptAppointment}
              text="Accept"
              fullWidth={isMobile}
            />
          )}
          {canMarkAsArrived && (
            <Button
              size={buttonSize}
              variant="reverse"
              onClick={onArrived}
              text="Arrived"
              fullWidth={isMobile}
            />
          )}
          {canCheckout && (
            <Button
              size={buttonSize}
              onClick={onCheckout}
              text="Checkout"
              fullWidth={isMobile}
            />
          )}
          {type === 'drawer' && canMarkAsNoShow && (
            <Button
              size={buttonSize}
              variant={isMobile ? 'link' : 'reverse'}
              onClick={onNoShow}
              text="No show"
            />
          )}
        </>
      )}
      {type === 'list' && dropdownItems.length > 0 && (
        <Dropdown
          id="actions"
          size="small"
          icon={MoreVertIcon}
          hideSelection
          items={dropdownItems}
          onClick={onDropdownClick}
        />
      )}
    </div>
  );
}

AppointmentActions.propTypes = {
  appointment: PropTypes.object,
  type: PropTypes.oneOf(['drawer', 'list']),
};
